import React, { useState, useEffect, useRef, useCallback } from 'react'
import { transformHtmlContent } from 'util/transformHtmlContent';
import { useUser } from 'util/UserContext'
import { useLocation, useNavigate } from 'react-router-dom'
import api from 'config/axiosConfig'
import { formatDateWithoutTimezone, formatRelativeTime } from 'config/dayjsConfig';
import RenderComments from './RenderComments'
import CommentInput from './CommentInput'
import Save from '../Entities/Save'
import './Comment.css'
import EntityActions from '../Entities/EntityActions'
import EntityAvatar from 'components/Entities/EntityAvatar'
import Report from 'components/Entities/Report'
import { Comment as CommentData } from 'util/constants'
import TooltipWrapper from 'ui/TooltipWrapper'
import ActorAvatar from 'components/Actors/ActorAvatar'
import ActorName from 'components/Actors/ActorName'
import { ACTOR_AVATAR_SIZES } from 'util/actorAvatarSizes'
import { toActorFromCommentAuthor } from 'util/actorIdentity'
import UrlPreviewCard from 'ui/UrlPreviewCard'
import { BookCheck, Ellipsis, Flag, Download, Loader2, Megaphone } from 'lucide-react';
//import 'components/Entities/EntityActions.css'
import { Pencil } from 'lucide-react';
import { Trash } from 'lucide-react';
import { CirclePlus } from 'lucide-react';
import { CircleMinus } from 'lucide-react';
import { MessageCircleReply } from 'lucide-react';
import { CircleCheck, CircleX } from 'lucide-react';
import { IterationCcw } from 'lucide-react';
import { useAnonEntityOwnership } from 'hooks/useAnonEntityOwnership';
import { downloadSingleKey } from 'util/anonTokenBackup';
import { 
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from 'ui/DropdownMenu'
import { Drawer, DrawerContent, DrawerTrigger } from 'ui/Drawer'
import DialogConfirmation from 'ui/DialogConfirmation'
import { toast } from 'sonner';
import { PrivacyLevel } from 'util/userUtils';
import EntityPrivacy from 'components/Entities/EntityPrivacy';
import MediaCarousel from 'ui/MediaCarousel';
import MediaViewer from 'components/Entities/MediaViewer';
import { SurfaceLayerProvider, useResolvedSurfaceLayer, useActiveSurfaceLayer } from "ui/layers";
import { createPortal } from 'react-dom';
import type { PollDraft } from 'types/poll';
import { buildDraftStorageKey } from 'util/draftStorage';
import PollCard from 'components/Comments/PollCard';
import type { PollResponse } from 'types/poll';
import { Coauthor, CoauthorSuggestion } from 'components/Coauthors/CoauthorSelect';
import CoauthorsModal from 'components/Common/CoauthorsModal';
import ShareActions from 'components/Common/ShareActions';
import EntityCompactCard from 'components/Entities/EntityCompactCard';
import LoadingSkeleton from 'ui/LoadingSkeleton';
import { apiRoutes } from "util/apiRoutes";
import PromoteEntityModal from 'components/Promotions/PromoteEntityModal';
import { getStoryUiStrings, resolveStoryLocale } from 'i18n/storyUi';
import { getDraftUiStrings, resolveDraftLocale } from 'i18n/draftUi';
import { buildCommentSharePath, buildShareRouteUrl } from "util/shareUrls";

interface CommentsProps extends CommentData {
    depth: number
    maxDepth: number
    isAllCollapsed: boolean
    isEntitySection: boolean
    onAddReply: (parent_id: number, content: string, privacy: PrivacyLevel, coauthors: CoauthorSuggestion[]) => void
    onUpdateComment: (id: number, content: string, privacy: PrivacyLevel, coauthors: CoauthorSuggestion[], addedCoauthors: CoauthorSuggestion[], removedCoauthors: CoauthorSuggestion[]) => void
    onDeleteComment: (id: number) => void
    showActions?: boolean | null | undefined
    showEntityCompactCard?: boolean // New prop to control entity compact card display
    showCommentHeader?: boolean // New prop to control comment header display
}

// Extend the CommentData type to include Teabate-specific properties in
// the properties object. Teabate arguments are identified by
// `comment.kind === 'argument'` (canonical); the stance lives under
// `properties.argument_type` and the debate round under `properties.level`.
declare module 'util/constants' {
    interface CommentProperties {
        privacy?: PrivacyLevel;
        coauthors?: Coauthor[];
        argument_type?: 'agree' | 'disagree';
        level?: number;
    }
}

const Comment: React.FC<CommentsProps> = (props) => {
    const {
        id,
        id_short,
        username,
        properties_public,
        user_id,
        user_id_short,
        created_at,
        content,
        properties,
        kind,
        replies,
        commentable_type,
        commentable_id,
        commentable_id_short,
        upvotes,
        downvotes,
        vote_count,
        parent_id,
        parent_id_short,
        root_id,
        root_id_short,
        updated_at,
        is_active,
        depth = 0,
        maxDepth = 6,
        isAllCollapsed = false,
        isEntitySection = false,
        viewer_relation,
        onAddReply,
        onUpdateComment,
        onDeleteComment,
        showActions = true,
        showEntityCompactCard = true,
        showCommentHeader = false,
    } = props;

    // Canonical teabate-argument check: `kind === 'argument'` is the
    // discriminator after `2026052600_argument_comment_kind.sql`.
    const isTeabateArgument = kind === 'argument';
    const { currentUser, setShowAuthModal, userIsMobile } = useUser()
    const [isAuthor, setIsAuthor] = useState<boolean>(false)
    const [isCollapsed, setIsCollapsed] = useState(false)
    const [isReplying, setIsReplying] = useState(false)
    const [isEditing, setIsEditing] = useState(false)
    const [isMediaViewerOpen, setIsMediaViewerOpen] = useState(false)
    
    // Check if user has anonymous edit token for this comment
    const hasAnonToken = useAnonEntityOwnership("comment", id, user_id, properties?.privacy)

    const [menuOpen, setMenuOpen] = useState(false)
    const [drawerOpen, setDrawerOpen] = useState(false)
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
    const [isPromoteModalOpen, setIsPromoteModalOpen] = useState(false)
    const commentStrings = getStoryUiStrings(resolveStoryLocale())
    const draftUi = getDraftUiStrings(resolveDraftLocale())
    const moreIconRef = useRef<HTMLButtonElement>(null)

    // Provide a layer context for modals opened while MediaViewer is active
    const activeLayer = useActiveSurfaceLayer();
    const mediaViewerLayer = useResolvedSurfaceLayer({ tier: "modal", layerBase: activeLayer?.childSurface });
    const modalLayerProviderValue = React.useMemo(
      () => ({ ...mediaViewerLayer, contentElement: null }),
      [mediaViewerLayer]
    );

    const [isReportModalOpen, setIsReportModalOpen] = useState(false);
    const hasReplies = replies && replies.length > 0
    const location = useLocation()
    const navigate = useNavigate()
    const entity = {
        id,
        id_short,
        name: null,
        eid: null,
        username,
        user_id,
        user_id_short,
        upvotes,
        downvotes,
        vote_count,
        content,
        commentable_type,
        commentable_id,
        commentable_id_short,
        created_at,
        is_active,
        replies,
        parent_id,
        parent_id_short,
        root_id,
        root_id_short,
        updated_at,
        properties: null,
        properties_public: properties_public,
        attributes: null,
        commentable_entity: undefined,
        viewer_relation: null,
    }

    const [isLoadingIssueName, setIsLoadingIssueName] = useState(false);

    const [commentAction, setCommentAction] = useState<'edit' | 'reply' | 'rebut' | null>(null);
    // entity.properties.coauthors is the canonical list of CONFIRMED
    // collaborators (see coauthor split 2026-04). Filter any lingering
    // `status: 'pending'` rows defensively so the public chip count and
    // modal never leak an unanswered invite.
    const [coauthors, setCoauthors] = useState<Coauthor[]>(
        (properties?.coauthors || []).filter(
            (c: { status?: string } | null | undefined) =>
                c !== null && c !== undefined && c.status !== 'pending'
        )
    )
    const [showCoauthorsModal, setShowCoauthorsModal] = useState(false)
    // Pending media added during edit flow (new media only)
    const [pendingEditMedia, setPendingEditMedia] = useState<FileList | null>(null)
    // Edit media state (deletions, reordering, new files)
    const [editMediaState, setEditMediaState] = useState<any>(null)
    // Pending media for replies
    const [pendingReplyMedia, setPendingReplyMedia] = useState<FileList | null>(null)
    const anonymousUserAvatar = `/avatea_incognito_nobg.png`
    
    // State for entity compact card
    const [entityForCard, setEntityForCard] = useState<any>(null)
    const [loadingEntity, setLoadingEntity] = useState(false)

    // Poll state (only loaded when comment has poll)
    const [pollData, setPollData] = useState<PollResponse | null>(null)
    const [pollLoading, setPollLoading] = useState(false)
    const [pollError, setPollError] = useState<string | null>(null)

    // Ref and state for detecting editor links and rendering previews
    const contentRef = useRef<HTMLDivElement | null>(null)
    const [linkPreviewUrls, setLinkPreviewUrls] = useState<string[]>([])

    // Helper: validate http(s) URLs
    const isHttpUrl = (value: string): boolean => {
        try {
            const parsed = new URL(value, window.location.origin)
            return parsed.protocol === 'http:' || parsed.protocol === 'https:'
        } catch {
            return false
        }
    }

    // Detect anchors with class "text-editor-link" in rendered content
    useEffect(() => {
        if (!contentRef.current) {
            setLinkPreviewUrls([])
            return
        }

        const anchors = Array.from(
            contentRef.current.querySelectorAll('a.text-editor-link')
        ) as HTMLAnchorElement[]

        const urls = anchors
            .map((a) => a.getAttribute('href'))
            .filter((href): href is string => Boolean(href))
            .map((href) => {
                try {
                    // Resolve relative paths against origin; keep absolute as-is
                    return new URL(href, window.location.origin).toString()
                } catch {
                    return null
                }
            })
            .filter((u): u is string => typeof u === 'string' && isHttpUrl(u))

        // De-duplicate while preserving order
        const seen = new Set<string>()
        const unique: string[] = []
        for (const u of urls) {
            if (!seen.has(u)) {
                seen.add(u)
                unique.push(u)
            }
        }

        setLinkPreviewUrls(unique)
    }, [content])

    /**
     * Fetch poll data for this comment when it has a poll.
     */
    const fetchPoll = useCallback(async () => {
        if (!properties?.has_poll) {
            setPollData(null)
            return
        }
        try {
            setPollLoading(true)
            setPollError(null)
            const response = await api.get(apiRoutes.polls.comment(id))
            setPollData(response.data)
        } catch (err) {
            setPollError('Failed to load poll')
            setPollData(null)
        } finally {
            setPollLoading(false)
        }
    }, [id, properties?.has_poll])

    useEffect(() => {
        if (properties?.has_poll) {
            void fetchPoll()
        }
    }, [properties?.has_poll, fetchPoll])

    const getIssueNameById = useCallback(async (id: number) => {
        try {
            const response = await api.get(apiRoutes.issues.byId(id));
            return response.data.name.replace(/ /g, '_');
        } catch (error) {
            console.error('Error fetching issue name:', error);
            return null;
        }
    }, []);

    const getCommentUrl = useCallback(async () => {
        return buildShareRouteUrl(buildCommentSharePath(id));
    }, [id]);

    const [commentUrl, setCommentUrl] = useState<string>('');

    useEffect(() => {
        const generateUrl = async () => {
            const url = await getCommentUrl();
            setCommentUrl(url);
        };
        generateUrl();
    }, [getCommentUrl]);

    // Fetch entity data for the compact card path (e.g. crosspost source,
    // non-entity-section quote previews). The teabate-argument context
    // header on the issue Discussion no longer relies on this fetch — it
    // delegates to `<EntityAvatar entityType="teabate" entityId={...}>`,
    // which manages its own TanStack-cached detail query and dedupes
    // across multiple arguments referencing the same teabate.
    useEffect(() => {
        const shouldFetchForCompactCard = showEntityCompactCard && !isEntitySection;
        if (!shouldFetchForCompactCard) {
            return;
        }

        const fetchEntityForCard = async () => {
            try {
                setLoadingEntity(true);

                // For crosspost comments, fetch the source entity
                if (properties?.is_cross_post && properties?.cross_post_source_type && properties?.cross_post_source_id) {
                    const response = await api.get(apiRoutes.entities.root, {
                        params: {
                            entityType: properties.cross_post_source_type,
                            entityIds: properties.cross_post_source_id,
                            limit: 1,
                            offset: 0
                        }
                    });

                    if (response.data && response.data.length > 0) {
                        setEntityForCard(response.data[0]);
                    }
                } else {
                    // For regular comments, fetch the commentable entity
                    const response = await api.get(apiRoutes.entities.root, {
                        params: {
                            entityType: commentable_type,
                            entityIds: commentable_id,
                            limit: 1,
                            offset: 0
                        }
                    });

                    if (response.data && response.data.length > 0) {
                        setEntityForCard(response.data[0]);
                    }
                }
            } catch (err) {
                console.error('Error fetching entity for compact card:', err);
            } finally {
                setLoadingEntity(false);
            }
        };

        fetchEntityForCard();
    }, [showEntityCompactCard, properties?.is_cross_post, properties?.cross_post_source_type, properties?.cross_post_source_id, isEntitySection, commentable_type, commentable_id]);

    useEffect(() => {
        setIsAuthor(currentUser?.username === username)
    }, [currentUser, username])

    useEffect(() => {
        setIsCollapsed(depth >= maxDepth || isAllCollapsed)
    }, [depth, maxDepth, isAllCollapsed])

    const handleCardClick = (event: React.MouseEvent<HTMLDivElement>) => {
        const element = event.target as HTMLElement;
        
        // Check if the clicked element or any of its parents have the ignore-card-click class
        const shouldIgnoreClick = element.closest('.ignore-card-click') !== null;
        
        // Return early if it's a link, read-more, or should be ignored
        if (element.tagName.toLowerCase() === 'a' || 
            element.classList.contains('read-more') || 
            shouldIgnoreClick) {
            return;
        }

        // Only proceed with navigation if not in entity section
        if (!isEntitySection) {
            setIsLoadingIssueName(true);
            let path;
            if (commentable_type === 'issue') {
                getIssueNameById(commentable_id).then(issueName => {
                    path = `/issue/${issueName}/comments/${id}`;
                    setIsLoadingIssueName(false);
                    navigate(path);
                });
            } else {
                path = `/${commentable_type}/${commentable_id}/comments/${id}`;
                setIsLoadingIssueName(false);
                navigate(path);
            }
        } else {
            return;
        }
    };

    const handleReplyClick = async () => {
        if (!currentUser) {
            setMenuOpen(false);
            setDrawerOpen(false);
            setShowAuthModal(true);
        } else {
            setMenuOpen(false);
            setDrawerOpen(false);
            if (location.pathname.includes(`${commentable_type}`)) {
                setCommentAction('reply');
                setIsReplying(true);
            } else {
                setIsLoadingIssueName(true);
                let path;
                if (commentable_type === 'issue') {
                    const issueName = await getIssueNameById(commentable_id);
                    path = `/issue/${issueName}/comments/${id}`;
                } else {
                    path = `/${commentable_type}/${commentable_id}/comments/${id}`;
                }
                setIsLoadingIssueName(false);
                navigate(path);
            }
        }
    };

    const handleRebutClick = async () => {
        if (!currentUser) {
            setShowAuthModal(true);
            return;
        }

        // Make sure we're not already replying or editing
        if (isReplying || isEditing) {
            return;
        }

        if (isTeabateArgument && properties?.argument_type) {
            // Close any open menus
            setMenuOpen(false);
            setDrawerOpen(false);
            
            // Only set one of these
            setCommentAction('rebut');
            setIsReplying(true);
        }
    };

    const handleEditClick = async () => {
        if (!currentUser) {
            setMenuOpen(false);
            setDrawerOpen(false);
            setShowAuthModal(true);
        } else {
            setMenuOpen(false);
            setDrawerOpen(false);
            if (location.pathname.includes(`${commentable_type}`)) {
                setCommentAction('edit');
                setIsEditing(true);
            } else {
                setIsLoadingIssueName(true);
                let path;
                if (commentable_type === 'issue') {
                    const issueName = await getIssueNameById(commentable_id);
                    path = `/issue/${issueName}/comments/${id}`;
                } else {
                    path = `/${commentable_type}/${commentable_id}/comments/${id}`;
                }
                setIsLoadingIssueName(false);
                navigate(path);
            }
        }
    };

    const handleDeleteClick = () => {
        if (!currentUser) {
            setShowAuthModal(true)
        } else {
            setMenuOpen(false)
            setDrawerOpen(false)
            setIsDeleteDialogOpen(true)
        }
    }

  

    const handleReportClick = () => {
        if (!currentUser) {
            setShowAuthModal(true)
        } else {
            setMenuOpen(false)
            setDrawerOpen(false)
            setIsReportModalOpen(true)
        }
    }




    const handleSaveClick = () => {
        setTimeout(() => {
            setMenuOpen(false)
            setDrawerOpen(false)
        }, 500)
    }

    const handleAddReply = (
        replyContent: string,
        privacy: PrivacyLevel,
        coauthors: CoauthorSuggestion[],
        _addedCoauthors: CoauthorSuggestion[],
        _removedCoauthors: CoauthorSuggestion[],
        pollDraft?: PollDraft | null
    ) => {
        if (id) {
            // Pass media files along with the reply
            (onAddReply as any)(id, replyContent, privacy, coauthors, pendingReplyMedia, pollDraft)
            setIsCollapsed(false)
            setIsReplying(false)
            setPendingReplyMedia(null)
        }
    }

    const handleUpdateComment = (
        updatedContent: string,
        privacy: PrivacyLevel,
        coauthors: CoauthorSuggestion[],
        addedCoauthors: CoauthorSuggestion[],
        removedCoauthors: CoauthorSuggestion[],
        _pollDraft?: PollDraft | null
    ) => {
        if (id) {
            // Pass media files and edit state along with the update
            (onUpdateComment as any)(id, updatedContent, privacy, coauthors, addedCoauthors, removedCoauthors, pendingEditMedia, editMediaState)
            setIsCollapsed(false)
            setIsEditing(false)
            setPendingEditMedia(null)
            setEditMediaState(null)
        }
    }

    const confirmDelete = () => {
        if (id) {
            onDeleteComment(id)
            setIsDeleteDialogOpen(false)
        }
    }

    const cancelDelete = () => {
        setIsDeleteDialogOpen(false)
    }

    const handleMouseEnterLine = (parentId: number) => {
        // highlight the parent line
        const parentLine = document.querySelector(`[data-line-id="${parentId}"]`);
        const parentTreeLine = document.querySelector(`[data-tree-line-id="${parentId}"]`);
        if (parentLine && parentTreeLine) {
            //parentTreeLine.classList.add('comment-tree-line-hovered');
        parentTreeLine?.classList.add('comment-tree-line-hovered');
            //parentTreeLine.classList.remove('comment-tree-line-unhovered');
        }      

        // highlight all child branch lines
        const childBranchLines = document.querySelectorAll(`[data-line-parent-id="${parentId}"]`);
        childBranchLines.forEach((el) => el.classList.add('comment-branch-line-hovered'));
        childBranchLines.forEach((el) => el.classList.remove('comment-branch-line-unhovered'));
      };
      
      const handleMouseLeaveLine = (parentId: number) => {
        // remove highlight from the parent line
        const parentLine = document.querySelector(`[data-line-id="${parentId}"]`);
        const parentTreeLine = document.querySelector(`[data-tree-line-id="${parentId}"]`);
        if (parentLine && parentTreeLine) {
            parentTreeLine.classList.remove('comment-tree-line-hovered');
            parentTreeLine.classList.add('comment-tree-line-unhovered');
        }
      
        // remove highlight from the child branch lines
        const childBranchLines = document.querySelectorAll(`[data-line-parent-id="${parentId}"]`);
        childBranchLines.forEach((el) => el.classList.add('comment-branch-line-unhovered'));
        childBranchLines.forEach((el) => el.classList.remove('comment-branch-line-hovered'));
      };

    const MoreButton = React.forwardRef<
        HTMLButtonElement,
        React.ButtonHTMLAttributes<HTMLButtonElement>
    >((props, ref) => (
        <button
            type="button"
            ref={ref}
            {...props}
            className="size-7 rounded-full flex justify-center items-center hover:bg-gray-200 dark:hover:bg-eoe-dark-icon-hover dark:text-eoe-dark-icon-stroke dark:hover:text-eoe-dark-icon-stroke-hover"
            disabled={!is_active}
        >
            <Ellipsis className="icon-stroke size-5"/>
        </button>
    ))
    MoreButton.displayName = 'MoreButton'

    const handleDownloadKey = async () => {
        setMenuOpen(false);
        setDrawerOpen(false);
        try {
            await downloadSingleKey("comment", id);
            toast.success("Edit key downloaded");
        } catch {
            toast.error("Failed to download key");
        }
    };

    const MoreOptions = () => {
        // User can edit/delete if they're the author OR have the anon token
        const canEdit = (isAuthor && currentUser?.id === user_id) || hasAnonToken;
        const commentPrivacy = typeof properties?.privacy === "string" ? properties.privacy : null;
        const isPublicOrAnonymous = commentPrivacy === "public" || commentPrivacy === "anonymous";
        // "Promote on ad breaks" only makes sense for the author of
        // public/anonymous content; we never offer it for follower-only
        // or members-only comments (those would be rejected at submit).
        const canPromote = Boolean(currentUser)
            && isAuthor
            && currentUser?.id === user_id
            && isPublicOrAnonymous;
        const handlePromoteClick = () => {
            setMenuOpen(false);
            setDrawerOpen(false);
            setIsPromoteModalOpen(true);
        };

        return (
        <>
            {!userIsMobile ? (
            <div className="flex flex-col">
                {canEdit && (
                    <>
                        <DropdownMenuItem onClick={handleEditClick}>
                            <div className="flex items-center gap-2 px-4 py-2">
                                <Pencil className="size-4 dark:text-eoe-dark-icon-stroke"/>
                                <span>Edit</span>
                            </div>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={handleDeleteClick}>
                            <div className="flex items-center gap-2 px-4 py-2">
                                <Trash className="size-4 dark:text-eoe-dark-icon-stroke"/>
                                <span>Delete</span>
                            </div>
                        </DropdownMenuItem>
                    </>
                    )}
                {hasAnonToken && (
                    <DropdownMenuItem onClick={handleDownloadKey}>
                        <div className="flex items-center gap-2 px-4 py-2">
                            <Download className="size-4 dark:text-eoe-dark-icon-stroke"/>
                            <span>Download edit key</span>
                        </div>
                    </DropdownMenuItem>
                )}
                {canPromote && (
                    <DropdownMenuItem onClick={handlePromoteClick}>
                        <div className="flex items-center gap-2 px-4 py-2">
                            <Megaphone className="size-4 dark:text-eoe-dark-icon-stroke"/>
                            <span>{commentStrings.promoteOnAdBreaks}</span>
                        </div>
                    </DropdownMenuItem>
                )}
                {currentUser && (
                    <div className='relative flex cursor-pointer select-none items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none transition-colors hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover focus:bg-eoe-icon-hover dark:focus:bg-eoe-dark-icon-hover focus:text-eoe-text-sub dark:focus:text-eoe-dark-text-sub data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0'>
                        <Save 
                            entityId={id} 
                            entityType="comment" 
                            currentUser={currentUser}
                            onSave={handleSaveClick}
                            className="block w-full flex-row items-center gap-2 px-4 py-2 rounded-md"
                        />
                    </div>
                )}

                <DropdownMenuItem onClick={handleReportClick}>
                    <div className="flex items-center gap-2 px-4 py-2">
                        <Flag className="size-4 dark:text-eoe-dark-icon-stroke"/>
                        <span>Report</span>
                    </div>
                </DropdownMenuItem>
            </div>
            ) : (
                    <>
                        {canEdit && (
                            <>
                                <button onClick={handleEditClick} className="flex items-center gap-2 px-4 py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
                                    <Pencil className="size-4"/>
                                    <span>Edit</span>
                                </button>
                                <button onClick={handleDeleteClick} className="flex items-center gap-2 px-4 py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
                                    <Trash className="size-4"/>
                                    <span>Delete</span>
                                </button>
                            </>
                        )}
                        {hasAnonToken && (
                            <button onClick={handleDownloadKey} className="flex items-center gap-2 px-4 py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
                                <Download className="size-4"/>
                                <span>Download edit key</span>
                            </button>
                        )}
                        {canPromote && (
                            <button onClick={handlePromoteClick} className="flex items-center gap-2 px-4 py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
                                <Megaphone className="size-4"/>
                                <span>{commentStrings.promoteOnAdBreaks}</span>
                            </button>
                        )}
                        <>
                        {currentUser && (
                                <Save 
                                    entityId={id} 
                                    entityType="comment" 
                                    currentUser={currentUser}
                                    onSave={handleSaveClick}
                                    className="flex flex-row items-start gap-2 px-4 py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer"
                                />
                        )}
                        </>

                        <button onClick={handleReportClick} className="flex items-center gap-2 px-4 py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
                            <Flag className="size-4"/>
                            <span>Report</span>
                        </button>
                    </>
                )}
        </>
    )}
    const handleCommentCancel = () => {
        setCommentAction(null);
        setIsReplying(false);
        setIsEditing(false);
    };

    const handleRebutArgument = async (content: string, argumentType: 'agree' | 'disagree', privacy: PrivacyLevel, coauthors: CoauthorSuggestion[]) => {
        try {
            if (!isTeabateArgument || !id) return;
            
            const teabateId = commentable_id;
            const nextLevel = properties.level !== undefined ? properties.level + 1 : 0;
            
            // Create the rebuttal
            const payload = {
                content,
                argument_type: argumentType,
                parent_id: id,
                is_anonymous: false,
                properties: {
                    privacy,
                    level: nextLevel
                }
            };
            
            // Submit the rebuttal
            const response = await api.post(apiRoutes.comments.teabateArgument(teabateId), payload);
            const newRebuttal = response.data;
            
            // Add coauthors if any
            if (coauthors.length > 0) {
                try {
                    await api.post(apiRoutes.coauthors.request("comment", newRebuttal.id), {
                        addedCoauthors: coauthors.map(c => ({
                            id: c.id,
                            type: c.type
                        }))
                    });
                } catch (err) {
                    console.error('Error adding coauthors to rebuttal:', err);
                }
            }
            
            // Reset state and refresh
            setIsReplying(false);
            setCommentAction(null);
            
            // Show success message
            toast.success('Rebuttal submitted successfully');
            
            // Trigger a UI refresh by calling onAddReply with special flag
            // This will signal the parent component to refresh comments
            // We send parent_id as -1 to indicate this is just a refresh trigger
            // The actual comment was already created via direct API call
            onAddReply(-1, "REFRESH_COMMENTS", privacy, []);
        } catch (err) {
            console.error('Error submitting rebuttal:', err);
            toast.error('Failed to submit rebuttal');
        }
    };

    const handleSaveReplyDraft = async (
        draftContent: string,
        privacy: PrivacyLevel,
        coauthors: CoauthorSuggestion[],
        pollDraft?: PollDraft | null
    ) => {
        const pollPayload = pollDraft
            ? {
                statement: pollDraft.statement,
                poll_type: pollDraft.poll_type,
                options: pollDraft.options.map((option) => ({
                    option_text: option.option_text,
                    option_entity_type: option.option_entity_type,
                    option_entity_id: option.option_entity_id,
                })),
            }
            : undefined;
        const nextLevel = properties?.level !== undefined ? properties.level + 1 : 0;
        await api.post(apiRoutes.comments.byCommentable(commentable_type, commentable_id), {
            content: draftContent,
            parent_id: id,
            status: "draft",
            kind: commentAction === "rebut" && properties?.argument_type ? "argument" : "comment",
            properties: {
                privacy,
                level: nextLevel,
                draft_coauthors: coauthors.map((coauthor) => ({ id: coauthor.id, type: coauthor.type })),
                ...(commentAction === "rebut" && properties?.argument_type
                    ? {
                        argument_type: properties.argument_type === "agree" ? "disagree" : "agree",
                    }
                    : {}),
            },
            poll: pollPayload,
        });
        toast.success(draftUi.draftSaved);
    };

    const renderCommentInputPortal = () => {
        if (!commentAction) return null;
        const replyDraftKey = currentUser
            ? buildDraftStorageKey("draft", "comment", commentAction, currentUser.id, id)
            : null;
        
        if ((commentAction === 'reply' || commentAction === 'rebut') && isReplying) {
            // For both reply and rebut, we can use the same portal approach
            return createPortal(
                commentAction === 'reply' ? (
                    <CommentInput
                        onSubmit={handleAddReply}
                        onCancel={handleCommentCancel}
                        buttonText="Reply"
                        action="reply"
                        commentData={{
                            id,
                            user_id,
                            username,
                            content,
                            updated_at,
                            created_at,
                            properties: {
                                coauthors: properties?.coauthors || []
                            }
                        }}
                        onMediaSelected={(files) => setPendingReplyMedia(files)}
                        onSaveDraft={handleSaveReplyDraft}
                        draftKey={replyDraftKey}
                        draftEnabled={Boolean(replyDraftKey)}
                    />
                ) : (
                    <CommentInput
                        onSubmit={(rebutContent, privacy, coauthors, _addedCoauthors, _removedCoauthors, _pollDraft) => {
                            if (isTeabateArgument && properties?.argument_type) {
                                const oppositeType = properties.argument_type === 'agree' ? 'disagree' : 'agree';
                                // Call the API to submit the rebuttal
                                handleRebutArgument(rebutContent, oppositeType, privacy, coauthors);
                            }
                        }}
                        onCancel={handleCommentCancel}
                        onSaveDraft={handleSaveReplyDraft}
                        buttonText="Submit Rebuttal"
                        action="rebut"
                        commentData={{
                            id,
                            user_id,
                            username,
                            content,
                            updated_at,
                            created_at,
                            kind,
                            properties: {
                                coauthors: properties?.coauthors || [],
                                argument_type: properties?.argument_type,
                                level: properties?.level !== undefined ? properties.level + 1 : 0
                            }
                        }}
                        draftKey={replyDraftKey}
                        draftEnabled={Boolean(replyDraftKey)}
                    />
                ),
                document.body
            );
        }
        
        if (commentAction === 'edit' && isEditing) {
            return createPortal(
                <CommentInput
                    onSubmit={handleUpdateComment}
                    onCancel={handleCommentCancel}
                    buttonText="Save"
                    initialContent={content}
                    initialPrivacy={properties?.privacy}
                    action="edit"
                    commentData={{
                        id,
                        user_id,
                        username,
                        content,
                        updated_at,
                        created_at,
                        properties: {
                            coauthors: properties?.coauthors || [],
                            media_files: (properties as any)?.media_files || []
                        }
                        }}
                            onMediaSelected={(files) => setPendingEditMedia(files)}
                            onEditMediaStateChange={(state) => setEditMediaState(state)}
                    onCoauthorsChange={(added: any, removed: any) => {
                        setCoauthors(prev => {
                            // Remove coauthors that were removed
                            const filtered = prev.filter(
                                (c: any) => !removed.some((r: any) => parseInt(r.id) === parseInt(c.id) && r.type === c.type)
                            );
                            return filtered;
                        });
                    }}
                />,
                document.body
            );
        }
        
        return null;
    };

    return (
        <div className={`eoe-dark bg-transparent w-full ${depth > 0 ? ' pl-[3px]' : ''} ${isEntitySection && !properties.is_cross_post ? ' my-4 px-4' : properties.is_cross_post ? ' mb-4 pl-2.5' : 'card-main'}`} onClick={handleCardClick}>
            {isMediaViewerOpen ? (
                <SurfaceLayerProvider value={modalLayerProviderValue}>
                    {isReportModalOpen && (
                        <Report
                            entityId={id}
                            entityType="comment"
                            open={isReportModalOpen}
                            onClose={() => setIsReportModalOpen(false)}
                        />
                    )}
                </SurfaceLayerProvider>
            ) : (
                <>
                    {isReportModalOpen && (
                        <Report
                            entityId={id}
                            entityType="comment"
                            open={isReportModalOpen}
                            onClose={() => setIsReportModalOpen(false)}
                        />
                    )}
                </>
            )}
            {/* Entity Compact Card - show for comments when enabled */}
            {showEntityCompactCard && !isEntitySection && entityForCard && !loadingEntity && (
                <div className="">
                    <EntityCompactCard
                        entity={{
                            ...entityForCard.attributes,
                            is_comment: properties?.is_cross_post ? false : true
                        }}
                        displayEntity={{
                            ...entityForCard.attributes,
                            is_comment: properties?.is_cross_post ? false : true
                        }}
                        entityType={properties?.is_cross_post ? 'crosspost' : commentable_type || 'comment'}
                        showMeta={true}
                        showActions={false}
                        onClick={() => {}}
                        crosspostData={properties?.is_cross_post ? {
                            sourceType: properties.cross_post_source_type,
                            sourceId: properties.cross_post_source_id,
                            targetType: commentable_type,
                            targetId: commentable_id,
                            sourceEntity: entityForCard,
                            targetEntity: undefined // Will be fetched by EntityAvatar if needed
                        } : undefined}
                    />
                </div>
            )}
            {showCommentHeader && loadingEntity ? (
                <LoadingSkeleton
                    avatarSize='h-6 w-6'
                    count={1}
                    variant='comment-card'
                    containerClassName='mx-4 pr-4'
                />
            ) : (
            <>
                {showCommentHeader
                    && isEntitySection
                    && isTeabateArgument
                    && properties?.level === 0
                    && commentable_type === 'teabate'
                    && commentable_id && (
                    /*
                     * Teabate-context header for an argument surfaced inline
                     * on the issue Discussion. We hand off to the shared
                     * `EntityAvatar` (entityChip + showLink) instead of the
                     * previous bespoke `<Link>` + badge so the row picks up
                     * the canonical teabate styling, link target, thumbnail,
                     * and identity model. Passing `entity={{ is_comment: true }}`
                     * flips the avatar's comment-context branch (parent
                     * issue → "Teabate" badge → teabate title); the
                     * `is_comment` flag is preserved through
                     * `mergeEntityDetailWithInline` via its allowlist. The
                     * avatar fetches the teabate detail itself via
                     * TanStack Query, so multiple arguments on the same
                     * teabate de-duplicate to a single network call.
                     */
                    <div className="ignore-card-click mb-1">
                        <EntityAvatar
                            entityType="teabate"
                            entityId={commentable_id}
                            entity={{ is_comment: true } as Record<string, unknown>}
                            entityChip={true}
                            showLink={true}
                            hasRing={false}
                            isInputField={false}
                        />
                    </div>
                )}
            </>
            )}
            <div className={`flex space-x-0 w-full bg-transparent ${isEntitySection ? 'card-main-profile-page' : showEntityCompactCard ? 'card-main' : 'my-1 p-4'}`}>
                <div className={`flex-col items-center shrink-0 ${showEntityCompactCard ? isEntitySection ? 'flex' : 'hidden' : 'flex'}`}>
                    <div className="relative flex items-center justify-center">
                        <div
                            id={`thread-branch-line-${parent_id}`}
                            data-comment-depth={depth}
                            data-comment-id={id}
                            data-comment-parent-id={parent_id}
                            data-comment-root-id={root_id}
                            data-line-parent-id={parent_id}
                            onMouseEnter={parent_id ? () => handleMouseEnterLine(parent_id) : undefined}
                            className={`thread-branch-line cursor-pointer ${depth > 0 || properties.is_cross_post ? 'absolute bottom-1/2 right-full h-[12px] w-[15.515px] rounded-bl-[9px] border-0 border-b-[2px] border-l-[2px] border-solid comment-branch-line-unhovered ' : ''}`}
                        ></div>
                        {/*
                         * Comment author avatar. For real authors we render
                         * the standardized `<ActorAvatar>` (story ring +
                         * profile link). Anonymous and deleted authors
                         * render a non-clickable mascot/initial fallback.
                         */}
                        {user_id && user_id !== 0 ? (
                            <ActorAvatar
                                actor={toActorFromCommentAuthor({
                                    user_id,
                                    username,
                                    display_name: null,
                                    properties_public,
                                })}
                                size={ACTOR_AVATAR_SIZES.chip}
                            />
                        ) : properties.privacy === 'anonymous' ? (
                            <span className="flex size-6 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover">
                                <img className="h-full w-full object-cover" src={anonymousUserAvatar} alt="Anonymous user" />
                            </span>
                        ) : (
                            <span className="flex size-6 flex-shrink-0 items-center justify-center rounded-full bg-eoe-bg-hover text-[10px] font-semibold text-eoe-text dark:bg-eoe-dark-bg-hover dark:text-eoe-dark-text">
                                {(username !== '[deleted]' ? (username || "Profile") : "D").slice(0, 1).toUpperCase()}
                            </span>
                        )}
                    </div>
                    {hasReplies && (
                        <div id={`thread-tree-line-${id}`} 
                            data-line-id={id}
                            onMouseEnter={() => handleMouseEnterLine(id)}
                            onMouseLeave={() => handleMouseLeaveLine(id)}
                            className={`group/thread-tree-line cursor-pointer relative w-5 ${!isCollapsed ? 'flex-grow mb-3 mt-[26px] ' : 'flex-grow mb-3 mt-2.5 '}`}>
                            <div 
                                data-tree-line-id={id}
                                className={`absolute ${isCollapsed ? 'bottom-2.5' : 'bottom-[26px]'} left-1/2 h-full w-[1px] -translate-x-1/2 transform comment-tree-line-unhovered`}
                            />
                            <button
                                onClick={() => setIsCollapsed(!isCollapsed)}
                                className="absolute inset-0"
                            >
                                <div
                                    data-tree-line-id={id}
                                    className={`flex absolute ${isCollapsed ? 'bottom-[2.6px]' : 'bottom-[18.75px]'} left-1/2 -translate-x-1/2 translate-y-1/2 transform flex-grow-0 comment-tree-line-unhovered bg-transparent group-hover/thread-tree-line:text-gray-700 dark:group-hover/thread-tree-line:text-white`}
                                >
                                    {isCollapsed ? (
                                        <CirclePlus data-tree-line-id={id} className="size-4 rounded-full" />
                                    ) : (
                                        <CircleMinus data-tree-line-id={id} className="size-4 rounded-full" />
                                    )}
                                </div>
                            </button>
                        </div>
                    )}
                </div>
                <div className={`flex flex-col flex-1 min-w-0 ${showEntityCompactCard ? 'pl-1' : 'ml-0'}`}>
                    <article className="w-full">
                        <div className={`ignore-card-click grid grid-cols-auto items-center w-full`}>
                            <div className={`group flex items-center ml-1 text-sm w-full overflow-hidden`}>
                                <div className="min-w-0 flex overflow-hidden">
                                {user_id && user_id !== 0 ? (
                                    <TooltipWrapper title={`View ${username}'s profile`}>
                                        <ActorName
                                            actor={toActorFromCommentAuthor({
                                                user_id,
                                                username,
                                                display_name: null,
                                                properties_public,
                                            })}
                                            label={username}
                                            className="card-username rounded-full group truncate inline-block"
                                        />
                                    </TooltipWrapper>
                                ) :
                                properties.privacy === 'anonymous' ? (
                                    <span className="card-username group line-clamp-1 inline-block max-w-full">
                                        [anonymous]
                                    </span>
                                ) : (
                                    <span className="card-username group line-clamp-1 inline-block max-w-full">
                                        [deleted user] 
                                    </span>
                                )}
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  <TooltipWrapper title={formatDateWithoutTimezone(created_at)}>
                                      <span className="card-date text-sm shrink-0 mr-2">
                                      ・{formatRelativeTime(created_at)}
                                      </span>
                                  </TooltipWrapper>
                                  <EntityPrivacy
                                    privacy={properties.privacy}
                                    viewerRelation={viewer_relation}
                                    onPrivacyChange={() => {}}
                                    actorType="user"
                                    editMode={false}
                                    entityId={id}
                                    entityType="comment"
                                    miniIcon={true}
                                    showDropdown={false}
                                    className="flex-shrink-0"
                                    publicOnly={commentAction === 'rebut' && isTeabateArgument && properties.argument_type ? true : false}
                                  />
                                  {properties?.privacy !== 'anonymous' && coauthors.length > 0 && (
                                      <button onClick={() => setShowCoauthorsModal(true)} className="flex flex-row items-center gap-1 text-xs shrink-0 bg-eoe-icon dark:bg-eoe-dark-icon hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover px-1 py-1 rounded-full cursor-pointer">
                                          <BookCheck className="size-3.5"/> {coauthors.length}
                                      </button>
                                  )}
                                  
                                  {/* Teabate Argument Chip */}
                                  {isTeabateArgument && properties?.argument_type && properties?.level !== undefined && (
                                      <div className="flex items-center gap-1">
                                          <span className={`teabate-argument-chip text-xs h-6 flex-shrink-0 ${
                                              properties.argument_type === 'agree'
                                                  ? 'teabate-argument-chip-agree'
                                                  : 'teabate-argument-chip-disagree'
                                          }`}>
                                              {properties.argument_type === 'agree' ? (
                                                  <CircleCheck className="size-4" />
                                              ) : (
                                                  <CircleX className="size-4" />
                                              )}
                                              Round {properties.level + 1}
                                          </span>
                                      </div>
                                  )}
                                </div>
                            </div>
                        </div>
                        {!isEditing && (
                            <>
                                <div ref={contentRef} className="comment-content w-full mx-1 my-1 text-sm">
                                    {transformHtmlContent(content)}
                                </div>
                                {linkPreviewUrls.length > 0 && (
                                    <div className="ignore-card-click my-2 flex flex-col gap-2">
                                        {linkPreviewUrls.map((u) => (
                                            <UrlPreviewCard key={u} url={u} />
                                        ))}
                                    </div>
                                )}
                                {/* Media gallery for comments */}
                                {properties?.media_files && Array.isArray((properties as any).media_files) && (properties as any).media_files.length > 0 && (
                                    <div className="ignore-card-click my-2">
                                        <MediaCarousel 
                                            mediaFiles={(properties as any).media_files} 
                                            className="w-full" 
                                            onMediaTap={() => setIsMediaViewerOpen(true)}
                                        />
                                    </div>
                                )}
                                {properties?.has_poll && (
                                    <div className="ignore-card-click my-2">
                                        {pollLoading && (
                                            <div className="poll-loading">
                                                <LoadingSkeleton count={1} height="h-6" width="w-full" margin="mt-0" rounded="rounded-xl" />
                                                <LoadingSkeleton count={3} height="h-12" width="w-full" margin="mt-2" rounded="rounded-xl" />
                                                <LoadingSkeleton count={1} height="h-8" width="w-1/3" margin="mt-3" rounded="rounded-xl" />
                                            </div>
                                        )}
                                        {pollError && (
                                            <div className="poll-error text-xs text-warning-red dark:text-dark-warning-red">
                                                {pollError}
                                            </div>
                                        )}
                                        {pollData && (
                                            <PollCard
                                                pollData={pollData}
                                                onRefresh={fetchPoll}
                                                contentAwardEntityType="comment"
                                                contentAwardEntityId={id}
                                                contentAwardAuthorUserId={user_id}
                                                contentAwardTargetLabel="comment"
                                            />
                                        )}
                                    </div>
                                )}
                                {showActions && (
                                <div className="flex flex-row items-center mr-0 min-w-[174px] mt-0">
                                    <EntityActions
                                        entity={entity}
                                        entityType="comment"
                                    />
                                    
                                    {/* Rebut Button - Only show for teabate arguments */}
                                    {isTeabateArgument && properties?.argument_type && (
                                        <button
                                            className="ignore-card-click text-xs flex cursor-pointer min-w-0 items-center justify-center gap-1 rounded-full px-2 py-1 font-semibold transition-colors hover:bg-gray-200 dark:hover:bg-eoe-dark-icon-hover dark:text-eoe-dark-icon-stroke dark:hover:text-eoe-dark-icon-stroke-hover"
                                            onClick={handleRebutClick}
                                            disabled={isLoadingIssueName}
                                        >
                                            <IterationCcw className="size-4 flex-shrink-0" />
                                            <span className="text-xs min-w-0 text-nowrap truncate">{isLoadingIssueName ? <Loader2 className="size-4 animate-spin" /> : 'Rebut'}</span>
                                        </button>
                                    )}
                                    
                                    <button
                                        className="ignore-card-click text-xs flex cursor-pointer min-w-0 items-center justify-center gap-1 rounded-full px-2 py-1 font-semibold transition-colors hover:bg-gray-200 dark:hover:bg-eoe-dark-icon-hover dark:text-eoe-dark-icon-stroke dark:hover:text-eoe-dark-icon-stroke-hover"
                                        onClick={handleReplyClick}
                                        disabled={isLoadingIssueName}
                                    >
                                        <MessageCircleReply className="size-4" />
                                        <span className="text-xs">{isLoadingIssueName ? <Loader2 className="size-4 animate-spin" /> : hasReplies && replies.length && replies.length > 0 ? replies.length : 'Reply'}</span>
                                    </button>
                                    <div className="ignore-card-click">
                                        <ShareActions 
                                            url={commentUrl}
                                            title={`Check out this comment on Societea`}
                                            text={`I found this interesting comment on Societea!`}
                                            sourceEntityType="comment"
                                            sourceEntityId={id}
                                            sourceEntityTitle={content}
                                            commentStyle={true}
                                            disableResteep={Boolean(properties?.is_cross_post)}
                                        />
                                    </div>
                                    <div className="icon-more text-xl">
                                    {userIsMobile ? (
                                        <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
                                            <DrawerTrigger asChild>
                                                <MoreButton ref={moreIconRef} />
                                            </DrawerTrigger>
                                            <DrawerContent title="Actions">
                                                <div className="flex flex-col">
                                                    <MoreOptions />
                                                </div>
                                            </DrawerContent>
                                        </Drawer>
                                    ) : (
                                        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen} modal={false}>
                                            <DropdownMenuTrigger asChild>
                                                <MoreButton ref={moreIconRef} />
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                                <MoreOptions />
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    )}
                                    </div>
                                </div>
                                )}
                            </>
                        )}
                    </article>
                    {/* Do not include duplicate CommentInput here */}
                    {hasReplies && !isCollapsed && (
                        <div className="comment-content card-description text-xs w-full">
                            <RenderComments
                                comments={replies}
                                depth={depth + 1}
                                maxDepth={maxDepth}
                                isAllCollapsed={isAllCollapsed}
                                onAddReply={onAddReply}
                                onUpdateComment={onUpdateComment}
                                onDeleteComment={onDeleteComment}
                                commentable_type={commentable_type}
                                commentable_id={commentable_id}
                                isEntitySection={isEntitySection}
                                showEntityCompactCard={false}
                            />
                        </div>
                    )}
                </div>
            </div>
            {isMediaViewerOpen ? (
                <SurfaceLayerProvider value={modalLayerProviderValue}>
                    {isDeleteDialogOpen && (
                        <DialogConfirmation 
                            title="Delete Comment" 
                            description="Are you sure you want to delete this comment?" 
                            isOpen={isDeleteDialogOpen} 
                            onConfirm={confirmDelete} 
                            onCancel={cancelDelete}
                        />
                    )}
                    {showCoauthorsModal && (
                        <CoauthorsModal
                            entityId={id}
                            entityType="comment"
                            onClose={() => setShowCoauthorsModal(false)}
                        />
                    )}
                    {isPromoteModalOpen && (
                        <PromoteEntityModal
                            isOpen={isPromoteModalOpen}
                            onClose={() => setIsPromoteModalOpen(false)}
                            entityType="comment"
                            entityId={id}
                            entityLabel={username ? `@${username}` : null}
                            sponsor={{
                                actorType: "user",
                                actorId: currentUser?.id ?? 0,
                            }}
                        />
                    )}
                </SurfaceLayerProvider>
            ) : (
                <>
                    {isDeleteDialogOpen && (
                        <DialogConfirmation 
                            title="Delete Comment" 
                            description="Are you sure you want to delete this comment?" 
                            isOpen={isDeleteDialogOpen} 
                            onConfirm={confirmDelete} 
                            onCancel={cancelDelete}
                        />
                    )}
                    {showCoauthorsModal && (
                        <CoauthorsModal
                            entityId={id}
                            entityType="comment"
                            onClose={() => setShowCoauthorsModal(false)}
                        />
                    )}
                    {isPromoteModalOpen && (
                        <PromoteEntityModal
                            isOpen={isPromoteModalOpen}
                            onClose={() => setIsPromoteModalOpen(false)}
                            entityType="comment"
                            entityId={id}
                            entityLabel={username ? `@${username}` : null}
                            sponsor={{
                                actorType: "user",
                                actorId: currentUser?.id ?? 0,
                            }}
                        />
                    )}
                </>
            )}
            {/* Render the comment input portal */}
            {renderCommentInputPortal()}
            {isMediaViewerOpen && (
                <MediaViewer
                    entity={{
                        ...props,
                        entity_type: "comment",
                        eid: `comment_${id}`,
                    } as any}
                    entityType="comment"
                    isOpen={isMediaViewerOpen}
                    onClose={() => setIsMediaViewerOpen(false)}
                    renderHeader={() => (
                        <div className="flex items-center gap-2 w-full">
                            <ActorAvatar
                                actor={toActorFromCommentAuthor({
                                    user_id,
                                    username,
                                    display_name: null,
                                    properties_public,
                                })}
                                size={ACTOR_AVATAR_SIZES.chip}
                            />
                            <div className="ml-auto flex items-center">
                                {userIsMobile ? (
                                    <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
                                        <DrawerTrigger asChild>
                                            <MoreButton ref={moreIconRef} />
                                        </DrawerTrigger>
                                        <DrawerContent title="Actions">
                                            <div className="flex flex-col">
                                                <MoreOptions />
                                            </div>
                                        </DrawerContent>
                                    </Drawer>
                                ) : (
                                    <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen} modal={false}>
                                        <DropdownMenuTrigger asChild>
                                            <MoreButton ref={moreIconRef} />
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                            <MoreOptions />
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                )}
                            </div>
                        </div>
                    )}
                />
            )}
        </div>
    )
}

export default Comment
